// The desktop docked panels' state on this device: open or out of the way, how
// wide, which tab. Per device like the appearance, remembered in localStorage:
// Prime beside a page (`prime-panel`) and the artifacts beside the lead's
// thread (`artifact-panel`) each keep their own.
import { create } from 'zustand'
import { PANEL_DEFAULT, parsePanel, type PanelState, type PanelTab } from '@/lib/panel'

type Store = PanelState & {
  setOpen(open: boolean): void
  toggle(): void
  setWidth(width: number): void
  setTab(tab: PanelTab): void
}

const panelStore = (key: string) => {
  const read = (): PanelState => {
    try {
      return parsePanel(localStorage.getItem(key))
    } catch {
      return { open: true, width: PANEL_DEFAULT, tab: 'thread' }
    }
  }
  const save = (s: PanelState) => {
    try {
      localStorage.setItem(key, JSON.stringify({ open: s.open, width: s.width, tab: s.tab }))
    } catch {
      /* private mode: this session only */
    }
  }
  return create<Store>((set, get) => {
    const put = (p: Partial<PanelState>) => {
      const { open, width, tab } = { ...get(), ...p }
      save({ open, width, tab })
      set(p)
    }
    return {
      ...read(),
      setOpen: (open) => put({ open }),
      toggle: () => put({ open: !get().open }),
      setWidth: (width) => put({ width }),
      setTab: (tab) => put({ tab }),
    }
  })
}

/** Prime beside an open page (docs/prime-panel.md). */
export const usePanel = panelStore('prime-panel')

/** The artifacts beside the lead's thread (docs/prime-panel.md); its tab is unused. */
export const useArtifactPanel = panelStore('artifact-panel')

/**
 * Whether a docked panel is on screen: while one is, the Shell gives the page
 * the whole width instead of its usual column (DockAside sets it).
 */
export const useDocked = create<{ on: boolean; set(on: boolean): void }>((set) => ({
  on: false,
  set: (on) => set({ on }),
}))
